import { OrderStatus } from '__generated__/atlas-types'

import { numberWithCommas } from 'utils/formatters'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import { Product, Size } from '../types'

/**
 * Returns the longest total cost string from an array of products.
 *
 * @param products - The array of products.
 * @returns The longest total cost string.
 */
export const getLongestTotalCostString = (products: Product[]) => {
  let longestTotalCostString = ''
  for (let prod of products) {
    const { colors } = prod
    for (let color of colors) {
      if (color.cost.label.length > longestTotalCostString.length) {
        longestTotalCostString = color.cost.label
      }
    }
  }
  return longestTotalCostString
}

/**
 * Calculates the width of the color total cost based on the length of the cost string.
 * If the cost string includes an empty cell content '-', the width is determined by the length of the cost string.
 * Otherwise, the width is determined by the length of the cost string.
 *
 * @param costStr - The cost string.
 * @returns The width of the color total cost.
 */
export const getColorTotalCostWidth = (costStr: string) => {
  const costLength = costStr.length
  if (costStr.includes(EMPTY_CELL_CONTENT)) {
    return costLength >= 19 ? '204px' : '158px'
  }
  return costLength >= 8 ? '126px' : '98px'
}

/**
 * Calculates the width of the color cost element based on the longest total cost string in the given array of products.
 *
 * @param products - The array of products.
 * @returns The width of the color cost element.
 */
export const getColorCostElementWidth = (products: Product[]) => {
  return getColorTotalCostWidth(getLongestTotalCostString(products))
}

export const AVG_CHAR_WIDTH_SIZE_LABEL = 7.3
const SIZE_MIN_WIDTH = 36

/**
 * Calculates the width of the color size element based on the length of the longest label
 * or quantity from a sizes array.
 *
 * @param sizes - The array of sizes.
 * @returns The width of the color size element.
 */
const getWidthForSize = (sizes: Size[]) => {
  if (!sizes.length) {
    return `${SIZE_MIN_WIDTH}px`
  }

  let longestLabel = ''
  let longestQuantity = ''

  for (let size of sizes) {
    if (size.label.length > longestLabel.length) {
      longestLabel = size.label
    }
    const formattedQuantity = numberWithCommas(size.quantity)
    if (formattedQuantity.length > longestQuantity.length) {
      longestQuantity = formattedQuantity
    }

    const formattedPrice = numberWithCommas(size.wholesalePrice)
    if (formattedPrice.length > longestQuantity.length) {
      longestQuantity = formattedPrice
    }
  }

  const labelAvgPixels =
    longestLabel.length > longestQuantity.length
      ? longestLabel.length * AVG_CHAR_WIDTH_SIZE_LABEL
      : longestQuantity.length * AVG_CHAR_WIDTH_SIZE_LABEL

  if (labelAvgPixels > SIZE_MIN_WIDTH) {
    return `${labelAvgPixels}px`
  }

  return `${SIZE_MIN_WIDTH}px`
}

/**
 * Calculates the width of the color size element based on the longest size string
 * from the sizes arrays of the given array of products.
 *
 * @param products - The array of products.
 * @returns The width of the color size element.
 */
export const getColorSizeElementWidth = (products: Product[]) => {
  const sizes = products.flatMap((prod) =>
    prod.colors.flatMap((color) => color.sizes),
  )
  return getWidthForSize(sizes)
}

/**
 * Determines whether the Edit button should be displayed for an order.
 * @param {OrderStatus | undefined} orderStatus - The status of the order.
 * @param {boolean} isRetailer - Indicates whether the user is a retailer.
 * @returns {boolean} - True if the Edit button should be displayed, otherwise false.
 */

export const areOrderProductsEditable = (
  orderStatus: OrderStatus | undefined,
  isRetailer: boolean,
  isEditApprovedShippedRestricted: boolean,
) => {
  if (orderStatus === undefined) {
    return false
  }

  const isNoteStatus = [OrderStatus.NOTES, OrderStatus.ALL_NOTES].includes(
    orderStatus,
  )
  if (isRetailer) {
    return isNoteStatus
  }

  if (isEditApprovedShippedRestricted) {
    const isRestricted = [
      OrderStatus.APPROVED,
      OrderStatus.SHIPPED,
      OrderStatus.CANCELLED,
    ].includes(orderStatus)

    return !isRestricted
  }

  /* This is not coherent with the name of the restriction because php is restricting to edit shipped
       orders independently of the isEditApprovedShippedRestricted permission */
  const isEditable = ![OrderStatus.CANCELLED, OrderStatus.SHIPPED].includes(
    orderStatus,
  )

  return isEditable
}
